// Components/Payout/AutoPay/ChangeStatus.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Formik, Field, Form } from "formik";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import {
    ModalHeader,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    ScrollableContent,
} from "../../Styles/InputStyles";
import SelectOptionModal from "../../SelectOptionModal";
import { toast } from "react-toastify";
import Config from "../../../Config";

const ChangeStatus = ({
    handleClose,
    changeStatusMutate,
    changeStatusLoading = false,
    selectedData
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

    // AutoPay status options - only Active and Cancelled
    const statusOptions = [
        { value: Config.AUTOPAY_STATUS.ACTIVE, label: 'Active' },
        { value: Config.AUTOPAY_STATUS.CANCELLED, label: 'Cancelled' },
    ];

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Get status label by value
    const getStatusLabel = useCallback((value) => {
        if (!value) return "Select Status";
        const status = statusOptions.find(s => s.value === value);
        return status ? status.label : "Select Status";
    }, []);

    // Get initial values from selectedData
    const getInitialValues = () => {
        if (!selectedData) {
            return {
                autopayId: "",
                status: "",
                statusLabel: "",
            };
        }

        const status = statusOptions.find(s => s.value === selectedData.status);

        return {
            autopayId: selectedData.id || "",
            status: selectedData.status || "",
            statusLabel: status ? status.label : "",
        };
    };

    const validate = (values) => {
        const errors = {};

        if (!values.status) {
            errors.status = "Status is required";
        }

        return errors;
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        // Prepare payload with autopayId and status
        const payload = {
            autopayId: parseInt(selectedData.id),
            status: parseInt(values.status),
        };

        // Call the mutation
        changeStatusMutate(payload);
    };

    const handleStatusSelect = (selectedValue, setFieldValue) => {
        if (selectedValue) {
            setFieldValue("status", selectedValue);
            const status = statusOptions.find(s => s.value === selectedValue);
            if (status) {
                setFieldValue("statusLabel", status.label);
            }
        } else {
            setFieldValue("status", "");
            setFieldValue("statusLabel", "");
        }
        setIsStatusModalOpen(false);
    };

    // Get initial values
    const initialValues = getInitialValues();

    // Get current status display
    const getCurrentStatusDisplay = () => {
        if (!selectedData?.status) return "Select Status";
        const status = statusOptions.find(s => s.value === selectedData.status);
        return status ? status.label : "Select Status";
    };

    return (
        <Container style={{ height }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Update AutoPay Status</h1>
                <button onClick={handleClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-120px)]">
                                <Wrapper>
                                    {/* Status Selection */}
                                    <InputGroup>
                                        <Label> Status *</Label>
                                        <FieldWrappers
                                            onClick={() => setIsStatusModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <Field
                                                type="text"
                                                name="statusLabel"
                                                readOnly
                                                value={getStatusLabel(values.status)}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                placeholder="Select Status"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>
                                        {errors.status && touched.status && (
                                            <ErrorText>{errors.status}</ErrorText>
                                        )}
                                    </InputGroup>
                                    {values?.status === Config.AUTOPAY_STATUS.CANCELLED && (
                                        <InfoBox>
                                            <InfoTitle>Important</InfoTitle>
                                            <InfoText>
                                                Once an AutoPay is cancelled, its status cannot be changed back to Active.
                                            </InfoText>
                                        </InfoBox>
                                    )}

                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={isSubmitting || changeStatusLoading}
                                >
                                    {isSubmitting || changeStatusLoading ? "Updating..." : "Update"}
                                </SubmitBtn>
                            </ActionSection>

                            {/* Status Selection Modal */}
                            <SelectOptionModal
                                isOpen={isStatusModalOpen}
                                onClose={() => setIsStatusModalOpen(false)}
                                title="Select Status"
                                options={statusOptions}
                                selectedValue={values.status}
                                onSelect={(value) => handleStatusSelect(value, setFieldValue)}
                                searchPlaceholder="Search status..."
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;
const InfoBox = tw.div`
    mt-2 p-4 
    bg-blue-50 
    border border-blue-200 
    rounded-lg
`;
const InfoTitle = tw.h4`
    text-sm font-medium text-blue-800 
    mb-1
`;
const InfoText = tw.p`
    text-sm text-blue-600
`;

export default ChangeStatus;